import type { WorksheetAssignee } from '@mathflat/domain/Worksheet'
import { tJosa } from '@mathflat/shared/i18n/tJosa'

import type { WorksheetQrQuery } from './worksheetQr'

export const QR_REJECT_MESSAGE = {
  'not-worksheet': '학습지 QR이 아닙니다',
  unreadable: 'QR을 인식하지 못했습니다. QR이 흐리거나 잘리지 않게 다시 찍어 주세요',
  'other-academy': '지금 로그인한 학원의 학습지가 아닙니다. 로그인한 학원을 확인해 주세요',
  deleted: '삭제된 학습지입니다',
  withdrawn: '퇴원한 학생의 학습지입니다',
  // 브랜드 번들이 로그인 뒤에 주입되므로 읽는 시점에 번역한다
  get unassigned() {
    return `${tJosa('출제', '이/가')} 취소된 학습지입니다`
  },
  'not-mine': '담당 학생의 학습지가 아닙니다',
} as const

export type QrReject = keyof typeof QR_REJECT_MESSAGE

export type QrScoringTarget =
  | { type: 'student'; assignee: WorksheetAssignee }
  | { type: 'roster'; assignees: WorksheetAssignee[] }
  | { type: 'empty' }
  | { type: 'reject'; reason: QrReject }

// 선생님이 볼 수 있는 학생(담당 필터가 걸린 재원생 목록)에 없으면 퇴원 또는 담당 아님이다
export const resolveQrScoringTarget = ({
  query,
  assignees,
  isVisibleStudent,
}: {
  query: WorksheetQrQuery
  assignees: WorksheetAssignee[]
  isVisibleStudent: (studentId: WorksheetAssignee['studentId']) => boolean
}): QrScoringTarget => {
  if (query.studentWorksheetId !== undefined || query.studentId !== undefined) {
    // 출제를 취소하고 다시 출제하면 출제건 id 가 바뀌므로 학생 id 로 한 번 더 찾는다
    const assignee =
      assignees.find((item) => item.studentWorksheetId === query.studentWorksheetId) ??
      assignees.find((item) => query.studentId !== undefined && item.studentId === query.studentId)
    if (!assignee) return { type: 'reject', reason: 'unassigned' }
    if (assignee.studentStatus !== 'ACTIVE') return { type: 'reject', reason: 'withdrawn' }
    if (!isVisibleStudent(assignee.studentId)) return { type: 'reject', reason: 'not-mine' }
    return { type: 'student', assignee }
  }

  const visible = assignees.filter(
    (item) => item.studentStatus === 'ACTIVE' && isVisibleStudent(item.studentId),
  )
  if (visible.length === 0) return { type: 'empty' }
  return { type: 'roster', assignees: visible }
}

export const getQrRejectFromErrorCode = (code: string | undefined): QrReject | undefined => {
  switch (code) {
    case 'WORKSHEET_PERMISSION_DENIED':
      return 'other-academy'
    case 'WORKSHEET_ALREADY_DELETED':
    case 'WORKSHEET_NOT_FOUND':
      return 'deleted'
    default:
      return undefined
  }
}
